"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import dataFormations from "../../lib/data/dataFormations";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRoad, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import Competences from "../../components/Competences";
import PropTypes from "prop-types";
import Breadcrumbs from "../../components/Breadcrumbs";

function AtoutsList({ atouts }) {
  return (
    <ul>
      {atouts.map((atout) => (
        <li key={atout} className="mb-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 mr-2" />
          <span>{atout}</span>
        </li>
      ))}
    </ul>
  );
}

AtoutsList.propTypes = {
  atouts: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function FormationsList({ formations }) {
  return (
    <ul className="mb-4 list-disc list-inside">
      {formations.map((formation, idx) => (
        <li key={formation.name + idx} className="mb-3">
          <a
            href={formation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold hover:underline hover:text-bleuciel">
            {formation.name}
          </a>
          {formation.dateObtained && (
            <span className="ml-2 text-gray-500 text-sm">
              ({formation.dateObtained})
            </span>
          )}
          {formation.recognizedBy && (
            <span className="ml-2 text-gray-500 text-sm">
              - {formation.recognizedBy}
            </span>
          )}
          {formation.description && (
            <div className="text-xs text-gray-400 mt-1">
              {formation.description}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

FormationsList.propTypes = {
  formations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string,
      dateObtained: PropTypes.string,
      recognizedBy: PropTypes.string,
      description: PropTypes.string,
    }),
  ).isRequired,
};

// Titre animé d'intro
function IntroTitle({ isClient }) {
  const title = (
    <>
      Spécialiste du Web depuis plus de 10 ans
      <br />
    </>
  );
  return isClient ? (
    <motion.h2
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{
        duration: 0.4,
        scale: {
          type: "spring",
          visualDuration: 0.4,
          bounce: 0.5,
        },
      }}
      className="text-3xl mb-5 leading-loose text-bleuciel">
      {title}
    </motion.h2>
  ) : (
    <h2 className="text-3xl mb-5 leading-loose text-bleuciel">{title}</h2>
  );
}

IntroTitle.propTypes = {
  isClient: PropTypes.bool.isRequired,
};

// Animation des lettres du titre
function AnimatedLetters({ isClient, letters, draw, sentence }) {
  if (isClient) {
    return (
      <motion.h2
        initial="hidden"
        animate="visible"
        className="text-3xl text-rose">
        {letters.map((letter, idlet) => (
          <motion.span
            key={idlet}
            custom={idlet}
            variants={draw}
            initial="hidden"
            animate="visible"
            className="inline-block">
            {letter}
          </motion.span>
        ))}
      </motion.h2>
    );
  }
  return <h2 className="text-3xl text-rose">{sentence}</h2>;
}

AnimatedLetters.propTypes = {
  isClient: PropTypes.bool.isRequired,
  letters: PropTypes.arrayOf(PropTypes.string).isRequired,
  draw: PropTypes.object.isRequired,
  sentence: PropTypes.string.isRequired,
};

export default function ParcoursClient() {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const ListAtouts = [
    "Esprit créatif",
    "Flexible, polyvalente",
    "Autonome et organisée",
    "Motivée par les défis",
    "Développeur FullStack : Front et BackEnd",
    "Capable de hiérarchiser les priorités",
  ];

  const sentence = `Développeur Full Stack - Javascript / TypeScript`;
  const letters = sentence
    .split("")
    .map((char) => (char === " " ? "\u00A0" : char));
  const draw = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.05,
      },
    }),
  };

  return (
    <div className="mx-10 px-4 my-10">
      <div className="flex">
        <div>
          <main id="main">
            <Breadcrumbs currentPage="A propos" />
            <h1 className="text-4xl my-5 flex items-center">
              <FontAwesomeIcon icon={faRoad} className="w-12 h-12 p-2 mr-2" /> A
              propos
            </h1>

            {/* Bloc animé d'intro refactorisé */}
            <IntroTitle isClient={isClient} />
            <AnimatedLetters
              isClient={isClient}
              letters={letters}
              draw={draw}
              sentence={sentence}
            />

            <div className="my-10 flex flex-col md:flex-row items-center">
              <div className="mr-5">
                <Image
                  src="/specialiste-communication-web-marseille.png"
                  alt="S. Fialon"
                  height={200}
                  width={200}
                  className="rounded-md mr-5"
                />
              </div>
              <div>
                <p>
                  Développeuse Web <strong>Full Stack</strong> avec plus de{" "}
                  <strong>15 ans d&apos;expérience</strong>, j&apos;ai construit
                  mon parcours aussi bien en agence, en entreprise qu&apos;en
                  freelance, ce qui m&apos;a permis de développer une forte
                  autonomie tout en sachant évoluer efficacement au sein
                  d&apos;équipes techniques.
                </p>
                <p>
                  J&apos;ai travaillé{" "}
                  <strong>5 ans en CDI en tant que développeuse unique</strong>,
                  puis <strong>3 ans en équipe</strong> dans une Web Agency,
                  avant d&apos;exercer <strong>6 ans en freelance</strong>. Ces
                  expériences m&apos;ont permis d&apos;intervenir sur
                  l&apos;ensemble du cycle de vie applicatif : conception,
                  développement (PHP, JavaScript), intégration, tests, mise en
                  production et maintenance.
                </p>
                <p>
                  Habituée à prendre en charge des projets de A à Z, j&apos;ai
                  travaillé sur des environnements variés, en développement
                  natif PHP, comme avec des CMS reconnus tels que{" "}
                  <strong>WordPress</strong> et <strong>Joomla</strong>.
                </p>
                <p>
                  Au fil de mon évolution professionnelle, j&apos;ai fait
                  évoluer mes compétences vers le{" "}
                  <strong>JavaScript moderne</strong> (React, VueJS, NodeJS),
                  puis le <strong>DevOps / DevSecOps</strong>, avec la mise en
                  place de <strong>pipelines CI/CD</strong> et des pratiques{" "}
                  <strong>Cloud</strong>, afin d&apos;améliorer la qualité, la
                  sécurité et la fiabilité des applications (SonarQube).
                </p>
              </div>
            </div>

            {/* Bloc compétences et formations */}
            <div className="mb-20 mt-10 skills" id="competences">
              <div className="flex flex-col lg:flex-row lg:items-top">
                <div>
                  <div className="section-title">
                    <h2>Mes atouts</h2>
                  </div>
                  <AtoutsList atouts={ListAtouts} />
                </div>
                <div className="md:ml-28">
                  <div className="section-title">
                    <h2>Titres & Certifications</h2>
                  </div>
                  <FormationsList formations={dataFormations} />
                </div>
              </div>
            </div>
            <div className="mb-20 mt-10 skills" id="competences">
              <Competences />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
